import type {Maybe} from "../../../bootstrap/common/types";
import type {CalendarEntryResponse} from "../feedService";
import {ELLIPSIS_IN_SQUARE_BRACKETS} from "../../../bootstrap/common/strings";

export class CalendarEntry {
    public constructor(private data: CalendarEntryResponse, private maxDescriptionLength: number, private usePageOverlay: boolean) {
    }

    public start(): CalendarEntryTimeBoundary {
        return new CalendarEntryTimeBoundary(
            new Date(this.data.start),
            this.data.showStartTime,
            this.data.startIsDST
        );
    }

    public ending(): CalendarEntryTimeBoundary {
        return new CalendarEntryTimeBoundary(
            new Date(this.data.ending),
            this.data.showEndingTime,
            this.data.endingIsDST
        );
    }

    public id(): string {
        return this.data.id;
    }

    public title(): string {
        return this.data.title;
    }

    public description(): Maybe<string> {
        return this.data.description?.truncateWordsAfter(this.maxDescriptionLength, ELLIPSIS_IN_SQUARE_BRACKETS);
    }

    public text(): Maybe<string> {
        return this.data.text;
    }

    public location(): Maybe<string> {
        return this.data.location;
    }

    public providesDetailInfo(): boolean {
        return !!this.data.linkType;
    }

    public link(): Maybe<string> {
        return this.data.link;
    }

    public imageUrl(): string {
        return this.data.imageUrl;
    }

    public imageIsResponsive(): boolean {
        return this.data.imageType === "responsiveImage";
    }

    public imageAltText(): string {
        return this.data.imageAltText;
    }

    public isInternalLink(): boolean {
        return this.data.linkType === "internal";
    }

    public isExternalLink(): boolean {
        return this.data.linkType === "external";
    }

    public isInfoLayerLink(): boolean {
        return this.data.linkType === "info-layer";
    }

    public infoLayerContent(): string {
        return this.data.richDescription ?? "";
    }

    public shouldUsePageOverlay(): boolean {
        return this.usePageOverlay;
    }

    public isIcsDownloadEnabled(): boolean {
        return this.data.icsDownloadEnabled ?? false;
    }
}

export class CalendarEntryTimeBoundary {
    public constructor(
        public raw: Date,
        public showTime: boolean,
        public isDST: boolean
    ) {
    }
}